// @ts-nocheck — TODO[type-debt]: 5 type mismatch reali (Combobox onChange / SaldoCardProps / Dispatch). Fix in sessione dedicata.
/**
 * Form registrazione carico merce in magazzino.
 *
 * Flusso a step (DEC-047):
 * 1. Seleziona fornitore (autocomplete)
 * 2. Seleziona documento (fattura/DDT del fornitore)
 * 3. Seleziona articolo, quantità, lotto, scadenza, costo
 * 4. Esito verifica merce (conforme/non conforme/parzialmente conforme)
 *
 * POST a /api/v1/magazzino/carico.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  type ComboboxOption,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@shell/AuthContext';
import {
  getUltimaUO,
  setUltimaUO,
  useUnitaAttive,
} from '@lib/unitaOrganizzativa';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Fornitore {
  id: string;
  ragione_sociale: string;
  partita_iva: string | null;
}

interface Documento {
  id: string;
  year: number;
  tipo: string;
  numero: string | null;
  data: string;
  importo_totale: number | null;
  cedente_denominazione: string | null;
  cessionario_denominazione: string | null;
  direzione: string;
}

interface Articolo {
  id: string;
  sku_interno: string;
  descrizione: string;
  giacenza: number;
}

interface CaricoFormData {
  fornitore_id: string;
  documento_id: string;
  articolo_id: string;
  unita_organizzativa_id: string;
  quantita: string;
  data_ingresso: string;
  numero_lotto: string;
  data_scadenza: string;
  data_produzione: string;
  costo_unitario: string;
  riferimento_esterno: string;
  note: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AUTH_HEADERS = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Restituisce la data odierna in formato YYYY-MM-DD. */
const todayISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EMPTY_FORM: CaricoFormData = {
  fornitore_id: '',
  documento_id: '',
  articolo_id: '',
  unita_organizzativa_id: '',
  quantita: '',
  data_ingresso: todayISO(),
  numero_lotto: '',
  data_scadenza: '',
  data_produzione: '',
  costo_unitario: '',
  riferimento_esterno: '',
  note: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Form carico merce con flusso a step:
 * fornitore → documento → articolo/lotto → verifica.
 */
const CaricoForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState<CaricoFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fornitori
  const [fornitori, setFornitori] = useState<Fornitore[]>([]);
  const [fornitoreSearch, setFornitoreSearch] = useState('');
  const [fornitoreLabel, setFornitoreLabel] = useState('');

  // Documenti del fornitore selezionato
  const [documenti, setDocumenti] = useState<Documento[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Articoli
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [articoloSearch, setArticoloSearch] = useState('');

  // Unita' organizzativa (negozio) - obbligatoria, default ultima usata
  const { unita, loading: loadingUnita, error: unitaError } = useUnitaAttive();
  const [unitaSearch, setUnitaSearch] = useState('');
  const [unitaPreselezionata, setUnitaPreselezionata] = useState(false);

  useEffect(() => {
    if (unitaPreselezionata || !user || unita.length === 0) return;
    const ultima = getUltimaUO(user.id);
    if (ultima && unita.some((u) => u.id === ultima)) {
      setForm((prev: CaricoFormData) => ({
        ...prev,
        unita_organizzativa_id: ultima,
      }));
    }
    setUnitaPreselezionata(true);
  }, [user, unita, unitaPreselezionata]);

  // -----------------------------------------------------------------------
  // Fetch fornitori (autocomplete)
  // -----------------------------------------------------------------------

  const fetchFornitori = useCallback(async (q: string) => {
    if (q.length < 1) {
      setFornitori([]);
      return;
    }
    try {
      const resp = await fetch(
        `/api/v1/fornitori?q=${encodeURIComponent(q)}&limit=20`,
        { headers: AUTH_HEADERS() },
      );
      if (resp.ok) {
        const data = await resp.json();
        setFornitori(data);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // -----------------------------------------------------------------------
  // Fetch documenti del fornitore (fatture passive + DDT)
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!form.fornitore_id) {
      setDocumenti([]);
      return;
    }

    const loadDocs = async () => {
      setLoadingDocs(true);
      try {
        // Fatture passive del fornitore
        const respFatt = await fetch(
          `/api/v1/fatture?direzione=passiva&soggetto_id=${form.fornitore_id}&limit=50`,
          { headers: AUTH_HEADERS() },
        );
        let docs: Documento[] = [];
        if (respFatt.ok) {
          const fatture = await respFatt.json();
          docs = fatture.map((f: Record<string, unknown>) => ({
            id: f.id,
            year: f.year,
            tipo: 'fattura',
            numero: f.numero,
            data: f.data,
            importo_totale: f.importo_totale,
            cedente_denominazione: f.cedente_denominazione,
            cessionario_denominazione: f.cessionario_denominazione,
            direzione: f.direzione,
          }));
        }

        // Documenti tipo DDT
        const respDdt = await fetch(
          `/api/v1/archivio?soggetto_id=${form.fornitore_id}&tipo=ddt&limit=50`,
          { headers: AUTH_HEADERS() },
        );
        if (respDdt.ok) {
          const ddts = await respDdt.json();
          const ddtDocs = ddts.map((d: Record<string, unknown>) => ({
            id: d.id,
            year: d.year,
            tipo: 'ddt',
            numero: d.dati_estratti && typeof d.dati_estratti === 'object'
              ? (d.dati_estratti as Record<string, unknown>).numero ?? null
              : null,
            data: d.created_at,
            importo_totale: null,
            cedente_denominazione: null,
            cessionario_denominazione: null,
            direzione: 'passiva',
          }));
          docs = [...docs, ...ddtDocs];
        }

        setDocumenti(docs);
      } catch {
        /* ignore */
      } finally {
        setLoadingDocs(false);
      }
    };

    loadDocs();
  }, [form.fornitore_id]);

  // -----------------------------------------------------------------------
  // Fetch articoli (autocomplete)
  // -----------------------------------------------------------------------

  const fetchArticoli = useCallback(async (q: string) => {
    if (q.length < 1) {
      setArticoli([]);
      return;
    }
    try {
      const resp = await fetch(
        `/api/v1/magazzino/articoli?q=${encodeURIComponent(q)}&limit=20`,
        { headers: AUTH_HEADERS() },
      );
      if (resp.ok) {
        const data = await resp.json();
        setArticoli(data);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleChange = (field: keyof CaricoFormData, value: string) => {
    setForm((prev: CaricoFormData) => ({ ...prev, [field]: value }));
  };

  const handleFornitoreSelect = (opt: ComboboxOption) => {
    setFornitoreLabel(opt.label);
    setFornitoreSearch('');
    setForm((prev: CaricoFormData) => ({
      ...prev,
      fornitore_id: opt.value,
      documento_id: '', // reset documento quando cambia fornitore
    }));
  };

  const handleArticoloSelect = (opt: ComboboxOption) => {
    setArticoloSearch(opt.label);
    handleChange('articolo_id', opt.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const payload: Record<string, unknown> = {
      articolo_id: form.articolo_id.trim(),
      quantita: Number(form.quantita),
      data_ingresso: form.data_ingresso,
      fornitore_id: form.fornitore_id.trim(),
      documento_id: form.documento_id.trim(),
      unita_organizzativa_id: form.unita_organizzativa_id.trim(),
      numero_lotto: form.numero_lotto.trim() || null,
      data_scadenza: form.data_scadenza || null,
      data_produzione: form.data_produzione || null,
      costo_unitario: form.costo_unitario ? Number(form.costo_unitario) : null,
      riferimento_esterno: form.riferimento_esterno.trim() || null,
      note: form.note.trim() || null,
    };

    try {
      const response = await fetch('/api/v1/magazzino/carico', {
        method: 'POST',
        headers: AUTH_HEADERS(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(
          errData?.detail ?? `Errore ${response.status}`,
        );
      }
      if (user) {
        setUltimaUO(user.id, form.unita_organizzativa_id);
      }
      navigate('/magazzino');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const isStepFornitoreOk = !!form.fornitore_id;
  const isStepDocumentoOk = !!form.documento_id;
  const isFormComplete =
    isStepFornitoreOk &&
    isStepDocumentoOk &&
    !!form.articolo_id &&
    !!form.unita_organizzativa_id &&
    !!form.quantita &&
    !!form.data_ingresso;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Carico merce
        </h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ---- STEP 1: Fornitore ---- */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                1
              </span>
              Fornitore
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-w-md space-y-2">
              <Label htmlFor="fornitore">Seleziona fornitore *</Label>
              <Combobox
                value={fornitoreSearch || fornitoreLabel}
                onInputChange={(v: string) => {
                  setFornitoreSearch(v);
                  setFornitoreLabel('');
                  if (form.fornitore_id) {
                    setForm((prev: CaricoFormData) => ({
                      ...prev,
                      fornitore_id: '',
                      documento_id: '',
                    }));
                  }
                  fetchFornitori(v);
                }}
                onSelect={handleFornitoreSelect}
                options={fornitori.map((f) => ({
                  value: f.id,
                  label: f.ragione_sociale,
                  sublabel: f.partita_iva ?? undefined,
                }))}
                placeholder="Cerca fornitore..."
              />
            </div>
          </CardContent>
        </Card>

        {/* ---- STEP 2: Documento ---- */}
        {isStepFornitoreOk && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                  2
                </span>
                Documento (fattura / DDT)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingDocs ? (
                <p className="text-sm text-muted-foreground">
                  Caricamento documenti…
                </p>
              ) : documenti.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nessun documento trovato per questo fornitore.
                  Registra prima una fattura passiva o carica un DDT.
                </p>
              ) : (
                <div className="space-y-2">
                  <Label>Seleziona documento *</Label>
                  <div className="max-h-60 space-y-1 overflow-y-auto">
                    {documenti.map((doc) => (
                      <button
                        key={`${doc.id}-${doc.year}`}
                        type="button"
                        className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          form.documento_id === doc.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/50'
                        }`}
                        onClick={() => handleChange('documento_id', doc.id)}
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {doc.tipo === 'ddt' ? 'DDT' : 'Fattura'}
                          </Badge>
                          <span>
                            {doc.numero ?? 'Senza numero'} — {doc.data}
                          </span>
                        </div>
                        {doc.importo_totale != null && (
                          <span className="text-muted-foreground">
                            €{' '}
                            {doc.importo_totale.toLocaleString('it-IT', {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ---- STEP 3: Dati carico ---- */}
        {isStepDocumentoOk && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
                  3
                </span>
                Dati carico
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="unita_organizzativa">
                    Negozio / Unita' organizzativa *
                  </Label>
                  <Combobox
                    value={(() => {
                      const sel = unita.find(
                        (u) => u.id === form.unita_organizzativa_id,
                      );
                      return sel ? sel.nome : unitaSearch;
                    })()}
                    onInputChange={(v: string) => {
                      setUnitaSearch(v);
                      if (form.unita_organizzativa_id) {
                        handleChange('unita_organizzativa_id', '');
                      }
                    }}
                    onSelect={(opt: ComboboxOption) => {
                      setUnitaSearch('');
                      handleChange('unita_organizzativa_id', opt.value);
                    }}
                    options={unita
                      .filter((u) => {
                        const q = unitaSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          u.nome.toLowerCase().includes(q) ||
                          (u.codice ?? '').toLowerCase().includes(q)
                        );
                      })
                      .map<ComboboxOption>((u) => ({
                        value: u.id,
                        label: u.nome,
                        sublabel: u.codice ?? u.tipo,
                      }))}
                    placeholder="Cerca negozio / unita organizzativa..."
                    loading={loadingUnita}
                  />
                  {!form.unita_organizzativa_id && (
                    <p className="text-xs text-muted-foreground">
                      Campo obbligatorio: seleziona il negozio a cui
                      attribuire il carico.
                    </p>
                  )}
                  {unitaError && (
                    <p className="text-xs text-destructive">
                      Impossibile caricare i negozi. Ricarica la pagina.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="articolo">Articolo *</Label>
                  <Combobox
                    value={articoloSearch}
                    onInputChange={(v: string) => {
                      setArticoloSearch(v);
                      if (form.articolo_id) {
                        handleChange('articolo_id', '');
                      }
                      fetchArticoli(v);
                    }}
                    onSelect={handleArticoloSelect}
                    options={articoli.map((a) => ({
                      value: a.id,
                      label: `${a.sku_interno} - ${a.descrizione}`,
                      sublabel: `Giacenza: ${a.giacenza}`,
                    }))}
                    placeholder="Cerca articolo (SKU o descrizione)..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantita">Quantità *</Label>
                  <Input
                    id="quantita"
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={form.quantita}
                    onChange={(e) => handleChange('quantita', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_ingresso">Data ingresso *</Label>
                  <Input
                    id="data_ingresso"
                    type="date"
                    value={form.data_ingresso}
                    onChange={(e) =>
                      handleChange('data_ingresso', e.target.value)
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="numero_lotto">Numero lotto</Label>
                  <Input
                    id="numero_lotto"
                    value={form.numero_lotto}
                    onChange={(e) =>
                      handleChange('numero_lotto', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_scadenza">Data scadenza</Label>
                  <Input
                    id="data_scadenza"
                    type="date"
                    value={form.data_scadenza}
                    onChange={(e) =>
                      handleChange('data_scadenza', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_produzione">Data produzione</Label>
                  <Input
                    id="data_produzione"
                    type="date"
                    value={form.data_produzione}
                    onChange={(e) =>
                      handleChange('data_produzione', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="costo_unitario">Costo unitario</Label>
                  <Input
                    id="costo_unitario"
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.costo_unitario}
                    onChange={(e) =>
                      handleChange('costo_unitario', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="riferimento_esterno">
                    Riferimento esterno
                  </Label>
                  <Input
                    id="riferimento_esterno"
                    value={form.riferimento_esterno}
                    onChange={(e) =>
                      handleChange('riferimento_esterno', e.target.value)
                    }
                    placeholder="Numero DDT, ordine…"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="note">Note</Label>
                <textarea
                  id="note"
                  className="flex min-h-[80px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  value={form.note}
                  onChange={(e) => handleChange('note', e.target.value)}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* ---- Actions ---- */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Annulla
          </Button>
          <Button type="submit" disabled={saving || !isFormComplete}>
            {saving ? 'Registrazione…' : 'Registra carico'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CaricoForm;
